import {
  ErrorBoundary,
  useAllPlayersSessionSummary,
  useSessionPlayers,
  useTeamSessionSummary
} from '@frontend/react'
import { Skeleton } from '@frontend/ui'
import { useMemo } from 'react'
import { DashboardChart } from '../../components/dashboard-chart'
import { ExportFooter } from '../../components/export-footer'
import { ExportHeader } from '../../components/export-header'
import { StatCard } from '../../components/stat-card'
import { useRegisterExport } from '../../lib/export-context'
import { useReportingState } from '../../lib/reporting-state'
import { buildDashboardCards } from './formatter'
import { DashboardPrint } from './index.print'

const dateFormatter = new Intl.DateTimeFormat(undefined, {
  year: 'numeric',
  month: 'long',
  day: 'numeric'
})

/**
 * Dashboard view — team-level session overview with stat cards and performance chart.
 *
 * STATE MANAGEMENT: All navigation state (team, session) lives in URL search params
 * via useSearchParams(). This enables bookmarkable URLs, browser back/forward, and
 * page refresh without losing state. The Angular app used RxJS BehaviorSubjects;
 * URL params cover the same cases with less code.
 */
export function Dashboard() {
  const {
    teamId,
    sessionId,
    team,
    session,
    sport,
    config,
    isLoading: contextLoading
  } = useReportingState()

  const { list: players, isLoading: playersLoading } = useSessionPlayers(
    teamId,
    sessionId
  )
  const { data: sessionSummary, isLoading: summaryLoading } =
    useTeamSessionSummary(teamId, sessionId)
  const { playerStats, isLoading: statsLoading } = useAllPlayersSessionSummary(
    teamId,
    sessionId
  )

  const cards = useMemo(
    () => buildDashboardCards(sessionSummary, playerStats, players, config),
    [sessionSummary, playerStats, players, config]
  )

  useRegisterExport(
    teamId && sessionId
      ? {
          sessionId,
          sessionDate: session?.date ?? '',
          sessionName: session?.name ?? '',
          sport
        }
      : null,
    [teamId, sessionId, session?.date, session?.name, sport]
  )

  const isLoading =
    contextLoading || playersLoading || summaryLoading || statsLoading

  return (
    <>
      <ExportHeader
        teamName={team?.name ?? ''}
        title='Dashboard'
        sessionType={session?.type}
        sessionDate={
          session ? dateFormatter.format(new Date(session.date)) : undefined
        }
        sessionName={session?.name}
      />
      <div className='space-y-6 px-4 py-6'>
        {!teamId || !sessionId ? (
          <div className='flex h-64 items-center justify-center'>
            <p className='text-muted-foreground'>
              Select a team and session to view the dashboard.
            </p>
          </div>
        ) : isLoading ? (
          <>
            <Skeleton className='h-80 w-full' />
            <div className='grid grid-cols-[repeat(auto-fill,minmax(226px,1fr))] gap-4'>
              {Array.from({ length: 7 }).map((_, i) => (
                <Skeleton key={i} className='h-40' />
              ))}
            </div>
          </>
        ) : !team || !session ? (
          <div className='flex h-full items-center justify-center p-8'>
            <p className='text-muted-foreground text-lg'>Session not found.</p>
          </div>
        ) : (
          <>
            {/* Screen layout — hidden when printing */}
            <div className='space-y-6 print:hidden'>
              <ErrorBoundary
                useUIFallback
                fallbackTitle='Failed to load performance chart'
              >
                <DashboardChart summary={sessionSummary} />
              </ErrorBoundary>

              {cards.length > 0 && (
                <ErrorBoundary
                  useUIFallback
                  fallbackTitle='Failed to load stat cards'
                >
                  <div className='grid grid-cols-[repeat(auto-fit,minmax(14rem,1fr))] gap-4'>
                    {cards.map((card) => (
                      <StatCard
                        key={card.label}
                        label={card.label}
                        value={card.value}
                        topPerformers={card.topPerformers}
                      />
                    ))}
                  </div>
                </ErrorBoundary>
              )}
            </div>

            {/* Print layout — plain text, shown only when printing */}
            <DashboardPrint summary={sessionSummary} cards={cards} />
          </>
        )}
      </div>
      <ExportFooter />
    </>
  )
}
